/**
 * Currency constants and type definitions.
 *
 * This module is the source of truth for currency data, providing:
 * - CURRENCY_DATA: The raw currency information
 * - CurrencyCode: Type and runtime object for currency codes
 * - MinorUnit: Union type of all possible minorUnits values
 * - MinorUnits: Lookup object with precise per-currency minor unit types
 */
// Source of truth for all currency data
export const CURRENCY_DATA = {
    AED: { name: 'United Arab Emirates dirham', minorUnits: 2 },
    AFN: { name: 'Afghan afghani', minorUnits: 2 },
    ALL: { name: 'Albanian lek', minorUnits: 2 },
    AMD: { name: 'Armenian dram', minorUnits: 2 },
    ANG: { name: 'Netherlands Antillean Guilder', minorUnits: 2 },
    AOA: { name: 'Angolan kwanza', minorUnits: 2 },
    ARS: { name: 'Argentine peso', minorUnits: 2 },
    AUD: { name: 'Australian dollar', minorUnits: 2 },
    AWG: { name: 'Aruban florin', minorUnits: 2 },
    AZN: { name: 'Azerbaijani manat', minorUnits: 2 },
    BAM: { name: 'Bosnia and Herzegovina convertible mark', minorUnits: 2 },
    BBD: { name: 'Barbados dollar', minorUnits: 2 },
    BDT: { name: 'Bangladeshi taka', minorUnits: 2 },
    BHD: { name: 'Bahraini dinar', minorUnits: 3 },
    BIF: { name: 'Burundian franc', minorUnits: 0 },
    BMD: { name: 'Bermudian dollar', minorUnits: 2 },
    BND: { name: 'Brunei dollar', minorUnits: 2 },
    BOB: { name: 'Boliviano', minorUnits: 2 },
    BOV: { name: 'Bolivian Mvdol (funds code)', minorUnits: 2 },
    BRL: { name: 'Brazilian real', minorUnits: 2 },
    BSD: { name: 'Bahamian dollar', minorUnits: 2 },
    BTN: { name: 'Bhutanese ngultrum', minorUnits: 2 },
    BWP: { name: 'Botswana pula', minorUnits: 2 },
    BYN: { name: 'Belarusian ruble', minorUnits: 2 },
    BZD: { name: 'Belize dollar', minorUnits: 2 },
    CAD: { name: 'Canadian dollar', minorUnits: 2 },
    CDF: { name: 'Congolese franc', minorUnits: 2 },
    CHE: { name: 'WIR euro (complementary currency)', minorUnits: 2 },
    CHF: { name: 'Swiss franc', minorUnits: 2 },
    CHW: { name: 'WIR franc (complementary currency)', minorUnits: 2 },
    CLF: { name: 'Unidad de Fomento (funds code)', minorUnits: 4 },
    CLP: { name: 'Chilean peso', minorUnits: 0 },
    CNH: { name: 'Chinese Yuan (offshore)', minorUnits: 2 },
    CNY: { name: 'Renminbi', minorUnits: 2 },
    COP: { name: 'Colombian peso', minorUnits: 2 },
    COU: { name: 'Unidad de Valor Real (UVR) (funds code)', minorUnits: 2 },
    CRC: { name: 'Costa Rican colon', minorUnits: 2 },
    CUP: { name: 'Cuban peso', minorUnits: 2 },
    CVE: { name: 'Cape Verdean escudo', minorUnits: 2 },
    CZK: { name: 'Czech koruna', minorUnits: 2 },
    DJF: { name: 'Djiboutian franc', minorUnits: 0 },
    DKK: { name: 'Danish krone', minorUnits: 2 },
    DOP: { name: 'Dominican peso', minorUnits: 2 },
    DZD: { name: 'Algerian dinar', minorUnits: 2 },
    EGP: { name: 'Egyptian pound', minorUnits: 2 },
    ERN: { name: 'Eritrean nakfa', minorUnits: 2 },
    ETB: { name: 'Ethiopian birr', minorUnits: 2 },
    EUR: { name: 'Euro', minorUnits: 2 },
    FJD: { name: 'Fiji dollar', minorUnits: 2 },
    FKP: { name: 'Falkland Islands pound', minorUnits: 2 },
    GBP: { name: 'Pound sterling', minorUnits: 2 },
    GEL: { name: 'Georgian lari', minorUnits: 2 },
    GHS: { name: 'Ghanaian cedi', minorUnits: 2 },
    GIP: { name: 'Gibraltar pound', minorUnits: 2 },
    GMD: { name: 'Gambian dalasi', minorUnits: 2 },
    GNF: { name: 'Guinean franc', minorUnits: 0 },
    GTQ: { name: 'Guatemalan quetzal', minorUnits: 2 },
    GYD: { name: 'Guyanese dollar', minorUnits: 2 },
    HKD: { name: 'Hong Kong dollar', minorUnits: 2 },
    HNL: { name: 'Honduran lempira', minorUnits: 2 },
    HTG: { name: 'Haitian gourde', minorUnits: 2 },
    HUF: { name: 'Hungarian forint', minorUnits: 2 },
    IDR: { name: 'Indonesian rupiah', minorUnits: 2 },
    ILS: { name: 'Israeli new shekel', minorUnits: 2 },
    INR: { name: 'Indian rupee', minorUnits: 2 },
    IQD: { name: 'Iraqi dinar', minorUnits: 3 },
    IRR: { name: 'Iranian rial', minorUnits: 2 },
    ISK: { name: 'Icelandic króna', minorUnits: 0 },
    JMD: { name: 'Jamaican dollar', minorUnits: 2 },
    JOD: { name: 'Jordanian dinar', minorUnits: 3 },
    JPY: { name: 'Japanese yen', minorUnits: 0 },
    KES: { name: 'Kenyan shilling', minorUnits: 2 },
    KGS: { name: 'Kyrgyzstani som', minorUnits: 2 },
    KHR: { name: 'Cambodian riel', minorUnits: 2 },
    KMF: { name: 'Comoro franc', minorUnits: 0 },
    KPW: { name: 'North Korean won', minorUnits: 2 },
    KRW: { name: 'South Korean won', minorUnits: 0 },
    KWD: { name: 'Kuwaiti dinar', minorUnits: 3 },
    KYD: { name: 'Cayman Islands dollar', minorUnits: 2 },
    KZT: { name: 'Kazakhstani tenge', minorUnits: 2 },
    LAK: { name: 'Lao kip', minorUnits: 2 },
    LBP: { name: 'Lebanese pound', minorUnits: 2 },
    LKR: { name: 'Sri Lankan rupee', minorUnits: 2 },
    LRD: { name: 'Liberian dollar', minorUnits: 2 },
    LSL: { name: 'Lesotho loti', minorUnits: 2 },
    LYD: { name: 'Libyan dinar', minorUnits: 3 },
    MAD: { name: 'Moroccan dirham', minorUnits: 2 },
    MDL: { name: 'Moldovan leu', minorUnits: 2 },
    MGA: { name: 'Malagasy ariary', minorUnits: 2 },
    MKD: { name: 'Macedonian denar', minorUnits: 2 },
    MMK: { name: 'Myanmar kyat', minorUnits: 2 },
    MNT: { name: 'Mongolian tögrög', minorUnits: 2 },
    MOP: { name: 'Macanese pataca', minorUnits: 2 },
    MRU: { name: 'Mauritanian ouguiya', minorUnits: 2 },
    MUR: { name: 'Mauritian rupee', minorUnits: 2 },
    MVR: { name: 'Maldivian rufiyaa', minorUnits: 2 },
    MWK: { name: 'Malawian kwacha', minorUnits: 2 },
    MXN: { name: 'Mexican peso', minorUnits: 2 },
    MXV: { name: 'Mexican Unidad de Inversion (UDI) (funds code)', minorUnits: 2 },
    MYR: { name: 'Malaysian ringgit', minorUnits: 2 },
    MZN: { name: 'Mozambican metical', minorUnits: 2 },
    NAD: { name: 'Namibian dollar', minorUnits: 2 },
    NGN: { name: 'Nigerian naira', minorUnits: 2 },
    NIO: { name: 'Nicaraguan córdoba', minorUnits: 2 },
    NOK: { name: 'Norwegian krone', minorUnits: 2 },
    NPR: { name: 'Nepalese rupee', minorUnits: 2 },
    NZD: { name: 'New Zealand dollar', minorUnits: 2 },
    OMR: { name: 'Omani rial', minorUnits: 3 },
    PAB: { name: 'Panamanian balboa', minorUnits: 2 },
    PEN: { name: 'Peruvian sol', minorUnits: 2 },
    PGK: { name: 'Papua New Guinean kina', minorUnits: 2 },
    PHP: { name: 'Philippine peso', minorUnits: 2 },
    PKR: { name: 'Pakistani rupee', minorUnits: 2 },
    PLN: { name: 'Polish złoty', minorUnits: 2 },
    PYG: { name: 'Paraguayan guaraní', minorUnits: 0 },
    QAR: { name: 'Qatari riyal', minorUnits: 2 },
    RON: { name: 'Romanian leu', minorUnits: 2 },
    RSD: { name: 'Serbian dinar', minorUnits: 2 },
    RUB: { name: 'Russian ruble', minorUnits: 2 },
    RWF: { name: 'Rwandan franc', minorUnits: 0 },
    SAR: { name: 'Saudi riyal', minorUnits: 2 },
    SBD: { name: 'Solomon Islands dollar', minorUnits: 2 },
    SCR: { name: 'Seychelles rupee', minorUnits: 2 },
    SDG: { name: 'Sudanese pound', minorUnits: 2 },
    SEK: { name: 'Swedish krona', minorUnits: 2 },
    SGD: { name: 'Singapore dollar', minorUnits: 2 },
    SHP: { name: 'Saint Helena pound', minorUnits: 2 },
    SLE: { name: 'Sierra Leonean leone (new leone)', minorUnits: 2 },
    SOS: { name: 'Somalian shilling', minorUnits: 2 },
    SRD: { name: 'Surinamese dollar', minorUnits: 2 },
    SSP: { name: 'South Sudanese pound', minorUnits: 2 },
    STN: { name: 'São Tomé and Príncipe dobra', minorUnits: 2 },
    SVC: { name: 'Salvadoran colón', minorUnits: 2 },
    SYP: { name: 'Syrian pound', minorUnits: 2 },
    SZL: { name: 'Swazi lilangeni', minorUnits: 2 },
    THB: { name: 'Thai baht', minorUnits: 2 },
    TJS: { name: 'Tajikistani somoni', minorUnits: 2 },
    TMT: { name: 'Turkmenistan manat', minorUnits: 2 },
    TND: { name: 'Tunisian dinar', minorUnits: 3 },
    TOP: { name: 'Tongan paʻanga', minorUnits: 2 },
    TRY: { name: 'Turkish lira', minorUnits: 2 },
    TTD: { name: 'Trinidad and Tobago dollar', minorUnits: 2 },
    TWD: { name: 'New Taiwan dollar', minorUnits: 2 },
    TZS: { name: 'Tanzanian shilling', minorUnits: 2 },
    UAH: { name: 'Ukrainian hryvnia', minorUnits: 2 },
    UGX: { name: 'Ugandan shilling', minorUnits: 0 },
    USD: { name: 'United States dollar', minorUnits: 2 },
    USN: { name: 'United States dollar (next day) (funds code)', minorUnits: 2 },
    UYI: { name: 'Uruguay Peso en Unidades Indexadas (URUIURUI) (funds code)', minorUnits: 0 },
    UYU: { name: 'Uruguayan peso', minorUnits: 2 },
    UYW: { name: 'Unidad previsional', minorUnits: 4 },
    UZS: { name: 'Uzbekistani sum', minorUnits: 2 },
    VED: { name: 'Venezuelan digital bolívar', minorUnits: 2 },
    VES: { name: 'Venezuelan sovereign bolívar', minorUnits: 2 },
    VND: { name: 'Vietnamese đồng', minorUnits: 0 },
    VUV: { name: 'Vanuatu vatu', minorUnits: 0 },
    WST: { name: 'Samoan tala', minorUnits: 2 },
    XAD: { name: 'Arab Accounting Dinar', minorUnits: 2 },
    XAF: { name: 'CFA franc BEAC', minorUnits: 0 },
    XAG: { name: 'Silver (one troy ounce)', minorUnits: 0 },
    XAU: { name: 'Gold (one troy ounce)', minorUnits: 0 },
    XBA: { name: 'European Composite Unit (EURCO) (bond market unit)', minorUnits: 0 },
    XBB: { name: 'European Monetary Unit (E.M.U.-6) (bond market unit)', minorUnits: 0 },
    XBC: { name: 'European Unit of Account 9 (E.U.A.-9) (bond market unit)', minorUnits: 0 },
    XBD: { name: 'European Unit of Account 17 (E.U.A.-17) (bond market unit)', minorUnits: 0 },
    XCD: { name: 'East Caribbean dollar', minorUnits: 2 },
    XCG: { name: 'Caribbean guilder', minorUnits: 2 },
    XDR: { name: 'Special drawing rights', minorUnits: 0 },
    XOF: { name: 'CFA franc BCEAO', minorUnits: 0 },
    XPD: { name: 'Palladium (one troy ounce)', minorUnits: 0 },
    XPF: { name: 'CFP franc (franc Pacifique)', minorUnits: 0 },
    XPT: { name: 'Platinum (one troy ounce)', minorUnits: 0 },
    XSU: { name: 'SUCRE', minorUnits: 0 },
    XTS: { name: 'Code reserved for testing', minorUnits: 0 },
    XUA: { name: 'ADB Unit of Account', minorUnits: 0 },
    XXX: { name: 'No currency', minorUnits: 0 },
    YER: { name: 'Yemeni rial', minorUnits: 2 },
    ZAR: { name: 'South African rand', minorUnits: 2 },
    ZMW: { name: 'Zambian kwacha', minorUnits: 2 },
    ZWG: { name: 'Zimbabwe Gold', minorUnits: 2 },
    ZWL: { name: 'Zimbabwean Dollar', minorUnits: 2 },
};
/**
 * Runtime object enabling syntax like `CurrencyCode.USD`.
 * Each property returns the currency code string itself.
 * @example CurrencyCode.USD === 'USD'
 */
const _CurrencyCode = Object.fromEntries(Object.keys(CURRENCY_DATA).map((code) => [code, code]));
export const CurrencyCode = Object.freeze(_CurrencyCode);
/**
 * Frozen lookup object providing the minorUnits for each currency code.
 * Unlike a function, this provides precise literal types at compile time.
 * @example MinorUnits.USD // type: 2, value: 2
 * @example MinorUnits.JPY // type: 0, value: 0
 * @example MinorUnits.BHD // type: 3, value: 3
 */
export const MinorUnits = Object.freeze(Object.fromEntries(Object.entries(CURRENCY_DATA).map(([code, data]) => [code, data.minorUnits])));
//# sourceMappingURL=CurrencyConstants.js.map